import React, { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styles from './styles.module.scss';

// assets

// services
import ErrorService from '../../../../../services/general/error';

// props
import { IPaginationResourceShort } from '../../../../../props/general';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack, TooltipHost } from '@fluentui/react';
import { useStore } from '../../../../../stores/root';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import Text from '../../../../typography/text';
import TeamForm from '../form';
import Pagination from '../../../../uiframeworks/pagination';
import GlobalConfig from '../../../../../config';
import ISalesTeamResourceShort from '../../../../../props/users/team';
import TeamService from '../../../../../services/users/team';
import DeleteTeamForm from '../delete';

interface ITeamsListProps {
}

const TeamsList: React.FC<ITeamsListProps> = (props: ITeamsListProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [teams, setTeams] = React.useState<ISalesTeamResourceShort[]>([]);
    const [activeTeam, setActiveTeam] = React.useState<ISalesTeamResourceShort | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            fieldName: "name",
            name: "Name",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: ISalesTeamResourceShort) => {
                return <Stack>
                    <Link to={`/users/team_members/${item.id}`}>
                        <Text>{item.name}</Text>
                    </Link>
                </Stack>;
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: ISalesTeamResourceShort) => {
                const menuItems: IContextualMenuItem[] = [
                    {
                        key: "update",
                        text: "Update details",
                        iconProps: { iconName: "Edit" },
                        onClick: () => {
                            setActiveTeam(item);
                            setActiveSurface('update');
                        }
                    },
                    {
                        key: "delete",
                        text: "Delete",
                        iconProps: { iconName: "Delete" },
                        onClick: () => {
                            setActiveTeam(item);
                            setActiveSurface('delete');
                        }
                    },
                ];

                return <Stack.Item styles={{ root: { padding: '2px 0px' } }}>
                    <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: menuItems
                        }} />
                    </Stack.Item>
                </Stack.Item>;
            },
        },
        {
            key: "description",
            fieldName: "description",
            name: "Description",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: ISalesTeamResourceShort) => {
                return <Stack>
                    <Text>{item.description}</Text>
                </Stack>;
            }
        },
    ];

    useEffect(() => {
        _onRetrieveTeams();
    }, []);

    useEffect(() => {
        _onRetrieveTeams();
    }, [keyword]);

    const _onRetrieveTeams = async (pageNumber?: number,) => {
        setLoaded(false);
        try {
            const qs: string[] = [];
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const results = await TeamService.retrieve(qs.join("&"));
            setTeams(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'administrators_list_error',
                text: 'Failed to retrieve administrators list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
            }
        });

        farItems.push({
            key: "register",
            text: "Create Team",
            iconProps: { iconName: "Add" },
            onRender: () => {
                return <PrimaryButton text={"Create Team"}
                    iconProps={{ iconName: "Add" }}
                    onClick={() => { setActiveSurface('create') }}
                    styles={{ root: { marginLeft: 10 } }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveTeam(undefined);

        if (refresh) { _onRetrieveTeams() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack tokens={{ childrenGap: 10 }}>
            {pagination && teams.length > 0 ? <Pagination goToPage={_onRetrieveTeams} currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " administrator(s) found"} /> : null}
            <Stack className={styles.list}>
                {
                    !loaded || (loaded && teams.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={teams}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && teams.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text>Team(s) not found</Text>
                </Stack> : null}
            </Stack>
            {pagination && teams.length > 0 ? <Pagination goToPage={_onRetrieveTeams} currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'create' ? <TeamForm onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update' && activeTeam ? <TeamForm teamId={activeTeam.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'delete' && activeTeam ? <DeleteTeamForm team={activeTeam} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default TeamsList;
